<template>
  <page-template
    :title="documentTitle"
    :description="documentDescription"
    :image="
      props.document.fields.cloudinaryImage
        ? fixImageUrlProtocol(props.document.fields.cloudinaryImage[0]?.url)
        : ''
    "
    og-type="article"
  >
    <!-- Header Intro Block -->
    <header-intro-block
      :title="documentTitle"
      :preamble="documentDescription"
      :body="
        props.document.fields.imageOtherAspect === true ? props.document.fields.body : undefined
      "
      :image="
        props.document.fields.cloudinaryImage
          ? fixImageUrlProtocol(props.document.fields.cloudinaryImage[0]?.url)
          : ''
      "
      :mascot="articleMascots"
      :kicker="articleKicker"
      :includes="props.document.includes"
      :button-text="props.document.fields.urlText"
      :button-url="props.document.fields.url"
      :contain-image="props.document.fields.imageOtherAspect === true"
    />

    <div class="container sm:relative">
      <div
        class="mx-auto flex flex-col items-start md:flex-row md:gap-8"
        :class="{ 'md:justify-center': !$slots.sidebar }"
      >
        <template v-if="!props.document.fields.imageOtherAspect">
          <main class="mb-4 mt-0 w-full rounded-[20px] bg-white px-8 py-8 md:mb-0 md:w-[60%]">
            <slot name="content"></slot>
            <template v-if="processedDocument">
              <!-- Handle Contentful Articles with Rich Text body (only if not moved to header) -->
              <template
                v-if="
                  'fields' in processedDocument &&
                  'body' in processedDocument.fields &&
                  !props.document.fields.imageOtherAspect
                "
              >
                <contentful-rich-text
                  :document="processedDocument.fields.body"
                  :list-variant="isRecipeArticle ? 'recipe' : 'default'"
                />
              </template>
              <!-- Handle documents with blocks -->
              <template v-if="'blocks' in processedDocument">
                <template v-for="(block, index) in processedDocument.blocks">
                  <!-- Handle generic textBlock content -->
                  <template v-if="isGenericTextBlock(block)">
                    <div :key="index" class="text-block-container">
                      <!-- Display mascots if present -->
                      <mascot-display
                        v-if="block.mascot && block.mascot.length > 0"
                        :mascot-ids="block.mascot"
                        v-bind="textBlockMascotConfig"
                        class="mb-4"
                      />
                      <contentful-rich-text
                        :document="(block as any).body"
                        :list-variant="isRecipeArticle ? 'recipe' : 'default'"
                      />
                    </div>
                  </template>
                  <!-- Handle form blocks based on _template or formType -->
                  <template v-if="getFormTemplateType(block) === 'competition_form'">
                    <lazy-competition-form :key="index" v-bind="block"></lazy-competition-form>
                  </template>
                  <template v-else-if="getFormTemplateType(block) === 'club_form'">
                    <lazy-club-form :key="index" v-bind="block"></lazy-club-form>
                  </template>
                  <template v-else-if="getFormTemplateType(block) === 'form'">
                    <lazy-rdk-app-form :key="index" v-bind="block"></lazy-rdk-app-form>
                  </template>
                  <template v-else-if="getFormTemplateType(block) === 'generic_form'">
                    <lazy-generic-form :key="index" v-bind="block"></lazy-generic-form>
                  </template>
                  <!-- Handle embedded images -->
                  <template v-else-if="block._template === 'embedded_image'">
                    <div :key="index" class="my-8">
                      <app-image
                        v-if="block.image"
                        class="h-auto w-full rounded-[20px]"
                        :url="fixImageUrlProtocol(block.image[0].url)"
                        responsive="width"
                        sizes="xs:100vw sm:100vw md:100vw lg:60vw xl:60vw 2xl:60vw"
                      ></app-image>
                    </div>
                  </template>
                </template>
              </template>
              <!-- Fallback for other document types -->
              <contentful-rich-text v-else :document="processedDocument" />
            </template>
          </main>
        </template>
        <aside v-if="useSlots().sidebar" class="flex w-full flex-col py-8 pt-0 md:w-[40%]">
          <slot name="sidebar"></slot>
        </aside>
      </div>

      <div class="container">
        <!-- Related Articles Section -->
        <RelatedArticles :current-article="props.document" />
      </div>
    </div>
  </page-template>
</template>

<script lang="ts" setup>
import type { Article as ContentfulArticle } from '@/types/contentful'
import { useMascots } from '@/composables/useMascots'
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'

const props = defineProps<{
  document: ContentfulArticle
}>()

const isRichTextDocument = (
  value: unknown,
): value is { nodeType: 'document'; content: unknown[] } => {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    (value as { nodeType?: string }).nodeType === 'document' &&
    Array.isArray((value as { content?: unknown[] }).content)
  )
}

const toFormText = (value: unknown) => {
  if (typeof value === 'string') return value
  if (isRichTextDocument(value)) return useRichText(value as any)
  return value
}

// Use the mascots composable
const { getMascotConfig } = useMascots()

// Process Contentful articles to include form blocks from articleContent and textBlocks from secondary
const processedDocument = computed(() => {
  // If it's a Contentful article with fields
  if ('fields' in props.document) {
    const article = props.document as ContentfulArticle
    const blocks: any[] = []

    // Process secondary field for textBlocks and CTA blocks (only if not using articleContent)
    if (
      article.fields.secondary &&
      Array.isArray(article.fields.secondary) &&
      (!article.fields.articleContent || article.fields.articleContent.length === 0)
    ) {
      const textBlockEntries = article.fields.secondary.filter(
        (entry: any) => entry.sys?.contentType?.sys?.id === 'textBlock',
      )

      textBlockEntries.forEach((textBlockEntry: any) => {
        blocks.push({
          _template: 'content',
          body: textBlockEntry.fields.body,
          blockType: textBlockEntry.fields.blockType,
          rubrik: textBlockEntry.fields.rubrik,
          mascot: textBlockEntry.fields.mascot || [],
        })
      })
    }

    // Process articleContent field for forms, images, and text blocks in the correct order
    if (article.fields.articleContent && Array.isArray(article.fields.articleContent)) {
      // Process entries in the order they appear in Contentful
      article.fields.articleContent.forEach((contentEntry: any) => {
        const contentType = contentEntry.sys?.contentType?.sys?.id

        if (contentType === 'articleForm') {
          // Determine template type from formType array
          const formType = contentEntry.fields.formType?.[0] || 'App'
          let templateType = 'form' // default

          switch (formType) {
            case 'App':
              templateType = 'form'
              break
            case 'Miniklubbar':
              templateType = 'club_form'
              break
            case 'Tävling':
              templateType = 'competition_form'
              break
            case 'Generiskt':
              templateType = 'generic_form'
              break
          }

          // Convert Rich Text fields to strings for form components
          const formFields = { ...contentEntry.fields }

          // Convert Rich Text fields to strings
          formFields.preamble = toFormText(formFields.preamble)
          formFields.disclaimer = toFormText(formFields.disclaimer)

          // Handle camelCase to lowercase field mapping for success fields
          if (formFields.successText) {
            formFields.successtext = toFormText(formFields.successText)
            delete formFields.successText
          } else if (formFields.successtext) {
            formFields.successtext = toFormText(formFields.successtext)
          }

          if (formFields.successTitle) {
            if (typeof formFields.successTitle === 'object') {
              formFields.successtitle = useRichText(formFields.successTitle)
            } else {
              formFields.successtitle = formFields.successTitle
            }
            delete formFields.successTitle
          } else if (
            formFields.successtitle &&
            typeof formFields.successtitle === 'object' &&
            formFields.successtitle.fields
          ) {
            formFields.successtitle = useRichText(formFields.successtitle)
          }

          blocks.push({
            _template: templateType,
            ...formFields,
          })
        } else if (contentType === 'articleImage') {
          blocks.push({
            _template: 'embedded_image',
            image: contentEntry.fields.cloudinaryImage,
            internalDescription: contentEntry.fields.internalDescription,
          })
        } else if (contentType === 'articleText') {
          // Handle articleText blocks - these are similar to textBlock but from articleContent
          blocks.push({
            _template: 'content',
            body: contentEntry.fields.body,
            blockType: contentEntry.fields.blockType,
            rubrik: contentEntry.fields.rubrik,
          })
        }
      })
    }

    // If we have blocks, transform the article
    if (blocks.length > 0) {
      const transformedArticle: any = { ...article }
      transformedArticle.blocks = blocks
      return transformedArticle
    }
  }

  return props.document
})

// Helper function to determine form template type from block
const getFormTemplateType = (block: any) => {
  // First check if _template is set (from our transformation)
  if (block._template) {
    return block._template
  }

  // Fallback: determine from formType array
  if (block.formType && Array.isArray(block.formType)) {
    const formType = block.formType[0]
    switch (formType) {
      case 'App':
        return 'form'
      case 'Miniklubbar':
        return 'club_form'
      case 'Tävling':
        return 'competition_form'
      case 'Generiskt':
        return 'generic_form'
      default:
        return 'form'
    }
  }

  return 'form' // default fallback
}

// Helper function to check if block is a generic textBlock or articleText
const isGenericTextBlock = (block: any) => {
  return (
    block.body &&
    // Original textBlock from secondary field
    ((block.blockType && Array.isArray(block.blockType) && block.blockType.includes('generell')) ||
      // articleText from articleContent field (any blockType or no blockType)
      block._template === 'content')
  )
}

// Get title from Contentful structure
const documentTitle = computed(() => {
  return props.document.fields.title
})

// Get description from Contentful structure
const documentDescription = computed(() => {
  return props.document.fields.preamble
})

// Get kicker from Contentful document
const articleKicker = computed(() => {
  // Use the kicker field directly from the document
  return props.document.fields.kicker || undefined
})

// Get article mascots from Contentful structure
const articleMascots = computed(() => {
  return props.document.fields.mascot || []
})

// Get mascot configuration for text blocks
const textBlockMascotConfig = computed(() => {
  return getMascotConfig('textBlock')
})

// Identify recipe articles (category slug === 'laga-vego')
const isRecipeArticle = computed(() => {
  const category = (props.document as any)?.fields?.category
  const slug = category?.fields?.slug || ''
  return slug === 'laga-vego'
})

const siteUrl = useRuntimeConfig().public.SITE_URL
const route = useRoute()

useSeoMeta({
  title: documentTitle.value,
  description: props.document.fields.preamble || undefined,
  ogImage:
    fixImageUrlProtocol(
      (props.document.fields.cloudinaryImage as any)?.fields?.cloudinaryImage[0]?.url,
    ) || undefined,
  ogUrl: siteUrl ? new URL(route.path, siteUrl).toString() : undefined,
})
</script>
